import React from 'react';
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Button, { Props as ButtonProps } from '@ff/ui/Button';
import Image from '@ff/ui/Image';
import Link from '@ff/ui/Link';

type ObjectFit = 'contain' | 'cover';

export interface Props {
  maxWidth?: string;
  maxHeight?: string;
  profileUrl?: string;
  profileUrlAlt?: string;
  profileRoundedCorner?: boolean;
  profileModal?: boolean;
  link?: string;
  readBio?: string;
  noBioHover?: boolean;
  aspectRatio?: string;
  profileObjectFit?: ObjectFit;
  onBioClick?: ButtonProps['onClick'];
}

export default function Profile(props: Props) {
  const {
    maxWidth,
    maxHeight,
    profileUrl,
    profileUrlAlt,
    profileRoundedCorner,
    profileModal,
    link,
    readBio,
    noBioHover,
    aspectRatio,
    profileObjectFit = 'cover',
    onBioClick
  } = props;

  return (
    <ProfileContainer
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      noBioHover={noBioHover}
      aspectRatio={aspectRatio}
      profileRoundedCorner={profileRoundedCorner}
      profileModal={profileModal}
      backgroundColor='black'
    >
      {/* Show Read More based on if link exists and if the modal is enabled */}
      {(link && !profileModal) ? (
        <Link href={link}>
          <ProfileImage objectFit={profileObjectFit} profileRoundedCorner={profileRoundedCorner} src={profileUrl} alt={profileUrlAlt || 'profile'} />
        </Link>
      ) : (
        <ProfileImage objectFit={profileObjectFit} profileRoundedCorner={profileRoundedCorner} src={profileUrl} alt={profileUrlAlt || 'profile'} />
      )}
      {profileModal && (
        <BioButton onClick={onBioClick}>{readBio}</BioButton>
      )}
    </ProfileContainer>
  );
}

const ProfileContainer = styled(Box)<{ noBioHover?: boolean; aspectRatio?: string, profileRoundedCorner?: boolean, profileModal?: boolean }>(({ noBioHover, aspectRatio = '1', profileRoundedCorner, profileModal }) => ({
  position: 'relative',
  borderRadius: profileRoundedCorner ? '12px' : '4px',
  aspectRatio,
  overflow: 'hidden',

  ...((!noBioHover && profileModal) && {
    '&:hover': {
      '& img': {
        transform: 'scale(1.3)',
        maskImage: 'linear-gradient(0deg, rgba(0, 0, 0, 0.50) 0%, rgba(0, 0, 0, 0.50) 100%)'
      },
      '& button': {
        opacity: '1'
      }
    }
  })
}));

const ProfileImage = styled(Image)<{ objectFit?: ObjectFit, profileRoundedCorner: boolean }>(({ objectFit, profileRoundedCorner }) => ({
  borderRadius: profileRoundedCorner ? '12px' : 0,
  transitionProperty: 'transform',
  transitionDuration: '800ms',
  width: '100%',
  height: '100%',
  objectFit,
  objectPosition: 'center'
}));

const BioButton = styled(Button)({
  position: 'absolute',
  bottom: '37px',
  left: '50%',
  transform: 'translateX(-50%)',
  opacity: '0',
  transitionProperty: 'opacity',
  transitionDuration: '800ms',
  borderRadius: '4px',
  border: '1px solid rgba(255, 255, 255, 0.69)',
  padding: '6px 24px',
  '& > div': {
    color: 'white',
    fontSize: '14px',
    lineHeight: '22px',
    fontWeight: 400,
    whiteSpace: 'nowrap'
  }
});
